// Route: /recordings — список записей лекций «Картотека».
// URL state: ?status= ?material= ?q= ?demo=. Вид (карточки/список) — в
// localStorage (useViewMode). См. docs/ui-spec/recordings.list.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, SectionHeader, Skeleton } from "@studyhub/ui";
import {
	IconCloudUpload,
	IconMicrophone,
	IconPlayerRecordFilled,
	IconPlus,
	IconSearch,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SECTION_TINT } from "@/components/navigation/section-icons";
import { type RecordingStatus, useRecordingsList } from "@/components/recordings/_api";
import { RecordingCard } from "@/components/recordings/recording-card";
import {
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useViewMode } from "@/hooks/use-view-mode";

const TILTS = [-1.2, 0.8, -0.6, 1, -0.9, 0.6] as const;

const searchSchema = z.object({
	status: z
		.enum(["queued", "recording", "paused", "uploading", "processing", "partial", "ready", "error"])
		.optional(),
	material: z.string().optional(),
	q: z.string().optional(),
	demo: z
		.enum([
			"default",
			"loading",
			"recording",
			"processing",
			"partial",
			"ready",
			"error",
			"network-drop",
			"empty",
		])
		.optional(),
});

export const Route = createFileRoute("/recordings/")({
	validateSearch: tolerantSearch(searchSchema),
	component: RecordingsListPage,
	pendingComponent: PagePendingFallback,
});

function RecordingsListPage(): React.ReactElement {
	const { t } = useTranslation("recordings");
	const intro = useSectionIntro("recordings");
	const search = useSearch({ from: "/recordings/" });
	const navigate = useNavigate({ from: "/recordings/" });
	const query = useRecordingsList();
	const [view, setView] = useViewMode("recordings");

	// `query.isLoading` уже прошёл через shim (честный, с потолком ожидания).
	// Повторный гейт флагом первичного синка ронял загрузку в false до прихода
	// строк, и список мигал «Записей нет» (см. `use-powersync-ready.ts`).
	const isLoading = search.demo === "loading" || query.isLoading;
	const isError = search.demo === "error" || query.isError;
	const allItems = useMemo(() => query.data?.items ?? [], [query.data]);

	const items = useMemo(() => {
		if (search.demo === "empty") return [];
		let list = allItems;
		if (search.status) list = list.filter((r) => r.status === (search.status as RecordingStatus));
		if (search.q) {
			const q = search.q.toLowerCase();
			list = list.filter((r) => r.title.toLowerCase().includes(q));
		}
		return list;
	}, [allItems, search]);

	// Presence-aware: uploading/processing на списке → mute notify.
	const pendingRecordingWatchKeys = useMemo(
		() =>
			allItems
				.filter((r) => r.status === "uploading" || r.status === "processing")
				.map((r) => markArtifactSeenKey("recording", r.id)),
		[allItems],
	);
	useArtifactWatching(pendingRecordingWatchKeys);

	const liveRecording = useMemo(
		() => allItems.find((r) => r.status === "recording" || r.status === "paused"),
		[allItems],
	);

	const hasPending = useMemo(
		() =>
			items.some(
				(i) =>
					i.status === "uploading" ||
					(i.chunksUploaded != null && i.chunksTotal != null && i.chunksUploaded < i.chunksTotal),
			),
		[items],
	);

	const setQuery = (value: string): void => {
		void navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });
	};

	const containerClass = view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconMicrophone />}
				category="time"
				tint={SECTION_TINT.recordings}
				title={t("list.sectionTitle")}
				description={
					items.length > 0 ? t("list.count", { count: items.length }) : t("list.subtitle")
				}
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button asChild className="cursor-pointer">
							<Link to="/recordings/new">
								<IconPlus size={16} />
								<span className="ml-2 hidden sm:inline">{t("list.newRecording")}</span>
							</Link>
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{liveRecording && (
				<Link
					to="/recordings/$id"
					params={{ id: liveRecording.id }}
					className="tap-press animate-enter flex items-center gap-3.5 rounded-[var(--radius-panel)] border border-destructive/25 bg-destructive/10 p-4 shadow-e1 transition-[transform,box-shadow] duration-fast ease-[var(--ease-spring)] hover:-translate-y-[2px] hover:shadow-e2 active:translate-y-0 cursor-pointer motion-reduce:transition-none motion-reduce:hover:translate-y-0"
				>
					<span className="grid size-11 shrink-0 place-items-center rounded-[11px] bg-destructive text-white [box-shadow:inset_0_1px_0_rgb(255_255_255/0.25)]">
						<IconPlayerRecordFilled size={20} />
					</span>
					<div className="min-w-0 flex-1">
						<div className="text-2xs font-semibold uppercase tracking-wider text-destructive">
							{t("list.liveNow")}
						</div>
						<div className="truncate text-sm font-medium text-foreground">
							{liveRecording.title}
						</div>
					</div>
					<Button
						size="sm"
						variant="outline"
						className="shrink-0 cursor-pointer pointer-coarse:h-11"
					>
						{t("list.liveOpen")}
					</Button>
				</Link>
			)}

			{(allItems.length > 0 || search.q) && !isError && (
				<ListToolbar
					search={{
						value: search.q ?? "",
						onChange: setQuery,
						placeholder: t("list.searchPlaceholder"),
					}}
					view={view}
					onViewChange={setView}
				/>
			)}

			{hasPending && (
				<div
					className="flex items-center gap-2.5 rounded-[var(--radius-card)] border border-warning/30 bg-warning/10 px-4 py-2.5 text-sm text-foreground"
					role="status"
				>
					<IconCloudUpload size={16} className="shrink-0 text-warning" />
					<span>{t("list.pendingUpload")}</span>
				</div>
			)}

			{isLoading ? (
				<RecordingsSkeleton view={view} />
			) : isError ? (
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorDescription")}
					action={
						<Button onClick={() => void query.refetch()} className="cursor-pointer">
							{t("list.retry")}
						</Button>
					}
				/>
			) : items.length === 0 ? (
				search.q ? (
					<EmptyState
						icon={<IconSearch />}
						title={t("list.searchEmptyTitle")}
						description={t("list.searchEmptyDescription", { query: search.q })}
					/>
				) : (
					<EmptyState
						icon={<IconMicrophone />}
						title={t("list.emptyTitle")}
						description={t("list.emptyDescription")}
						action={
							<Button asChild className="cursor-pointer">
								<Link to="/recordings/new">
									<IconPlus size={16} className="mr-2" />
									{t("list.startRecording")}
								</Link>
							</Button>
						}
					/>
				)
			) : (
				<div className={containerClass}>
					{items.map((rec, i) => (
						<RecordingCard
							key={rec.id}
							recording={rec}
							view={view}
							tilt={view === "grid" ? TILTS[i % TILTS.length] : undefined}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function RecordingsSkeleton({ view }: { view: "grid" | "list" }) {
	if (view === "grid") {
		return (
			<div className={LIST_PAGE_GRID} aria-busy="true">
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton key={i} className="h-36 rounded-[var(--radius-card)]" />
				))}
			</div>
		);
	}
	return (
		<div className={LIST_PAGE_STACK} aria-busy="true">
			{Array.from({ length: 6 }).map((_, i) => (
				<Skeleton key={i} className="h-[72px] rounded-[var(--radius-card)]" />
			))}
		</div>
	);
}
